import { ApplicationRef, Injectable, Injector, inject } from '@angular/core';
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { CapturedImage, PhotoCaptureOptions, PhotoCapturePort } from './photo-capture.port';
import { CameraCaptureComponent } from './camera-capture/camera-capture';

/**
 * Web provider for PHOTO_CAPTURE (design D2, revised). Mounts the existing
 * CameraCaptureComponent (ADR-8, live getUserMedia capture) inline into the
 * caller-supplied `hostElement` via a CDK DomPortalOutlet, instead of a
 * floating CDK Overlay attached to <body>.
 *
 * Reverted from an Overlay-hosted design: CameraCaptureComponent's own
 * `flex-1`/`aspect-[4/3]` classes always assumed the width/height context it
 * had pre-platform-seam, when it was declared inline in photo-step's
 * template (itself nested in activation-flow-layout's `max-w-lg`/`min-h-dvh`
 * page shell). An Overlay pane attached to <body> has none of that ancestor
 * context — confirmed broken in a real browser: the denied/error message
 * rendered with correct DOM content but was visually collapsed/invisible.
 * DomPortalOutlet mounts into an element that already lives inside the page's
 * real layout, so the component gets real dimensions again — restoring the
 * original no-button, no-overlay behavior confirmed correct pre-migration.
 */
@Injectable({ providedIn: 'root' })
export class WebPhotoCaptureService implements PhotoCapturePort {
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(Injector);

  // Restores the pre-platform-seam web UX: no button, capture starts as soon
  // as the step needs a photo (design addendum to D2).
  readonly autoStart = true;

  async capture(options?: PhotoCaptureOptions): Promise<CapturedImage | null> {
    const hostElement = options?.hostElement;
    if (!hostElement) return null;

    const outlet = new DomPortalOutlet(hostElement, this.appRef, this.injector);
    const portal = new ComponentPortal(CameraCaptureComponent, null, this.injector);
    const cmp = outlet.attachComponentPortal(portal);
    cmp.setInput('tip', options?.tip);

    try {
      // Inline hosting has no backdrop/dismiss affordance (matches the
      // original pre-migration web behavior) — the promise resolves only
      // once a frame is actually captured. 'denied'/'error' states render
      // their own retry() control and never emit `captured`.
      return await new Promise<CapturedImage | null>(resolve => {
        cmp.instance.captured.subscribe(img => resolve(img));
      });
    } finally {
      // Load-bearing: triggers CameraCaptureComponent.ngOnDestroy -> stop(),
      // which releases the MediaStream. Skipping it leaves the camera LED on.
      outlet.dispose();
    }
  }
}
